/**
 * Defers server save for short text (quest title) until blur / Enter / explicit flush.
 * While IME composition is active, input events still update local state but never commit.
 */

export type DeferredCommit = {
  /** Local value changed; does not call onCommit. */
  noteEdit(): void;
  /** Persist pending edits when appropriate (blur, navigation, flush). */
  commit(): void;
  /** True when there are local edits not yet committed. */
  readonly pending: boolean;
  bindComposition(el: EventTarget): void;
};

export function createDeferredCommit(onCommit: () => void): DeferredCommit {
  let composing = false;
  let pending = false;

  const noteEdit = () => {
    pending = true;
  };

  const commit = () => {
    if (composing || !pending) return;
    pending = false;
    onCommit();
  };

  const bindComposition = (el: EventTarget) => {
    el.addEventListener("compositionstart", () => {
      composing = true;
    });
    el.addEventListener("compositionend", () => {
      composing = false;
      noteEdit();
    });
  };

  return {
    noteEdit,
    commit,
    get pending() {
      return pending;
    },
    bindComposition,
  };
}

/** Update local state and preview on each input; debounced save skips IME composition. */
export function bindLiveTextControl(
  el: EventTarget,
  handlers: { onInput(): void; onSync(): void; scheduleSave(): void },
): void {
  let composing = false;
  el.addEventListener("compositionstart", () => {
    composing = true;
  });
  el.addEventListener("compositionend", () => {
    composing = false;
    handlers.onInput();
    handlers.onSync();
    handlers.scheduleSave();
  });
  el.addEventListener("input", () => {
    handlers.onInput();
    handlers.onSync();
    if (!composing) handlers.scheduleSave();
  });
}
